import { InlineAlertType } from '@tableau/widgets';
import React from 'react';

import { openReactActionDialog, ReactActionDialogBodyProps } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISetSmbVolumesResult } from '~src/settings/dataconnect-settings/serverApi/requests/SetSmbVolumesParams';
import { DomainUpdateType, IUpdateBaasEdgePoolResult } from '~src/settings/dataconnect-settings/serverApi/requests/UpdateBaasEdgePoolRequest';
import { BaasEdgePoolAndCluster } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePoolAndCluster';

import { Domain, DomainID } from '../../types/Domain';
import { DomainPermission } from '../../types/DomainPermission';
import { DomainTableItem } from '../../types/DomainTableItem';
import { DomainType } from '../../types/DomainType';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { FilePathValidationStatus } from '../../types/FilePathValidationStatus';
import { PoolID } from '../../types/PoolID';
import { UserDefinedPoolModel } from '../../types/PoolModel';
import { createDomainTableItems, validateDomainName, validateFilePath } from '../../utils/DomainUtilities';
import { getGeneralErrorMessage } from '../../utils/EdgePoolErrorUtilities';
import { AddNewDomainDialogBody } from './AddNewDomainDialogBody';


export function openAddNewDomainDialog(
  onAddDomainDialogSave: (domainType: DomainType, domainName: string, fileServer: string, username: string, password: string) => Promise<IResult>,
  onTestConnection: (fileServer: string, username: string, password: string) => Promise<{ message: string, messageType: InlineAlertType }>,
  otherDomainNames: ReadonlyArray<string>,
  poolDomainNames: ReadonlyArray<string>,
  poolID: PoolID,
  poolName: string
) {
  const action = (_props: AddNewDomainDialogInputProps, state: AddNewDomainDialogState) => {
    return onAddDomainDialogSave(state.domainType, state.domainName, state.filePath, state.username, state.password);
  };

  const onTestConnectionAction = (state: AddNewDomainDialogState) => {
    return onTestConnection(state.filePath, state.username, state.password);
  };


  const dialogInstance = openReactActionDialog({
    title: Messages.dataConnectSiteSettingsAddNewDomain({POOL_NAME: poolName}),
    dialogBodyStyle: { maxHeight: '600px', overflowY: 'auto' },
    bodyComponent: AddNewDomainDialog,
    initialBodyProps: { isEditDomainDialog: false, onTestConnection: onTestConnectionAction, otherDomainNames, poolDomainNames, poolID, poolName},
    initialDialogState: {
      domainName: '',
      domainPermission: DomainPermission.ALLOW,
      domainValidationStatus: DomainValidationStatus.NO_ERROR,
      domainType: DomainType.DOMAIN,
      filePath: '',
      filePathValidationStatus: FilePathValidationStatus.NO_ERROR,
      username: '',
      password: ''
    },
    actionDisabled: (_props, state) => {
      return (state.domainType === DomainType.FILESERVER && state.filePathValidationStatus !== FilePathValidationStatus.NO_ERROR)
      || (state.domainType === DomainType.DOMAIN && state.domainValidationStatus !== DomainValidationStatus.NO_ERROR);
    },
    actionName: Messages.save(),
    action: action,
    isActionDelete: false,
    dialogWidth: TabDialogWidth.width500px,
    dismissDialogOnActionFailed: false,
  });

  return Promise.resolve(dialogInstance.result);
}

export type AddNewDomainDialogInputProps = Readonly<{
  otherDomainNames: ReadonlyArray<string>;
  poolDomainNames: ReadonlyArray<string>;
  poolID: PoolID;
  poolName: string;
  onTestConnection: (state: AddNewDomainDialogState) => Promise<{ message: string, messageType: InlineAlertType }>;
  isEditDomainDialog: boolean;
}>;

export type AddNewDomainDialogState = Readonly<{
  domainName: string;
  domainType: DomainType;
  filePath: string;
  domainPermission: DomainPermission;
  domainValidationStatus: DomainValidationStatus;
  filePathValidationStatus: FilePathValidationStatus;
  username: string;
  password: string;
  oldPassword?: string;
  existingDomainName?: string;
  existingFilePath?: string;
}>;

export type AddNewDomainDialogProps = ReactActionDialogBodyProps<
  AddNewDomainDialogInputProps,
  AddNewDomainDialogState
>;

export const AddNewDomainDialog = (props: AddNewDomainDialogProps): JSX.Element => {
  const setDomainValidationStatus = (validationStatus: DomainValidationStatus) => props.updateDialogState({ domainValidationStatus: validationStatus });
  const setDomainName = (changedDomainName: string) => props.updateDialogState({ domainName: changedDomainName });
  const setFilePath = (changeFilePath: string) => props.updateDialogState({ filePath: changeFilePath });
  const setFilePathValidationStatus = (validationStatus: FilePathValidationStatus) => props.updateDialogState({ filePathValidationStatus: validationStatus });
  const setDomainType = (changedDomainType: DomainType) => props.updateDialogState({ domainType: changedDomainType });
  const setDomainPermission = (changedDomainPermission: DomainPermission) => props.updateDialogState({ domainPermission: changedDomainPermission });
  const setUsername = (username: string) => props.updateDialogState({ username: username });
  const setPassword = (password: string) => props.updateDialogState({ password: password });


  const onDomainTypeChange = (changedDomainType: DomainType) => {
    setDomainType(changedDomainType);
  };

  const onDomainNameChange = (changedDomainName: string) => {
    setDomainValidationStatus(validateDomainName(changedDomainName, props.otherDomainNames, props.poolDomainNames));
    setDomainName(changedDomainName);
  };

  const onFilePathChange = (changeFilePath: string) => {
    setFilePathValidationStatus(validateFilePath(props.dialogState.existingFilePath, changeFilePath, props.otherDomainNames, props.poolDomainNames));
    setFilePath(changeFilePath);
  };

  const onDomainPermissionChange = (changedDomainPermission: DomainPermission) => {
    setDomainPermission(changedDomainPermission);
  };

  const onUsernameChange = (username: string) => {
    setUsername(username);
  };
  const onPasswordChange = (password: string) => {
    setPassword(password);
  };

  const onTestConnectionClicked = () => {
    return props.onTestConnection(props.dialogState);
  };

  return (
    <AddNewDomainDialogBody
      isEditDomainDialog={props.isEditDomainDialog}
      onDomainNameChange={onDomainNameChange}
      domainName={props.dialogState.domainName}
      onDomainPermissionChange={onDomainPermissionChange}
      domainPermission={props.dialogState.domainPermission}
      assignedPoolID={props.poolID}
      assignedPoolName={props.poolName}
      domainValidationStatus={props.dialogState.domainValidationStatus}
      onDomainTypeChange={onDomainTypeChange}
      domainType={props.dialogState.domainType}
      onFilePathChange={onFilePathChange}
      filePath={props.dialogState.filePath}
      filePathValidationStatus={props.dialogState.filePathValidationStatus}
      onUserNameChange={onUsernameChange}
      onPasswordChange={onPasswordChange}
      username={props.dialogState.username}
      password={props.dialogState.password}
      onTestConnectionClicked={onTestConnectionClicked}
      testConnectionDisabled={props.dialogState.filePathValidationStatus !== FilePathValidationStatus.NO_ERROR}
      isEditPassword={props.isEditDomainDialog}
      />
  );
};


export function openAddPoolDomainDialog(poolId: PoolID,
                  poolName: string, userDefinedPools: Record<string, UserDefinedPoolModel>,
                  bridgeDomains: Record<string, Domain>, refreshPoolsAndClusters: () => void) {

  const onAddDomainDialogSave = (domainType: DomainType, domainName: string, fileServer: string, username: string, password: string): Promise<IResult> => {
    let domainNameToUse = domainName;
    if (domainType === DomainType.FILESERVER) {
      const domain = fileServer.match(/^([A-Za-z0-9\-_.]+)[\\/]/);
      domainNameToUse = domain ? domain[1] : fileServer;
    }
    return BaasEdgePoolAndCluster.updateBaasPool({
      poolId: poolId,
      displayName: null,
      domainChange: { change: DomainUpdateType.Add, domainId: domainNameToUse as DomainID },
      DataConnectSettingsVersion: '0'
    }).then((result: IUpdateBaasEdgePoolResult) => {
      if (result.success) {
        // file server case add the file server config
        if (domainType === DomainType.FILESERVER) {
          // Convert subfolders to full paths if this is a file server
          let fullPath: string = '//' + fileServer;

          // Extract domain from username (format: domain\username, domain/username)
          let extractedDomain = '';
          let extractedUsername = username;
          if (username) {
            // Check for domain\username or domain/username format
            const domainMatch = username.match(/^([^\\\/]+)[\\\/](.+)$/);
            if (domainMatch && domainMatch[1]) {
              extractedDomain = domainMatch[1];
              extractedUsername = domainMatch[2];
            }
          }

          return BaasEdgePoolAndCluster.setSmbVolumes({
            poolId: poolId,
            path: fullPath,
            domain: extractedDomain,
            username: extractedUsername,
            password: password,
            dryRun: false
          }).then((result: ISetSmbVolumesResult) => {
            if (result.success) {
              ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsAddDomainFileServerSuccess() });
              refreshPoolsAndClusters();
            }
            if (result.errors) {
              ToasterService.instance.pop({
                type: ToastType.Error,
                text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
              });
              // failed to add the file server config, remove the domain from the pool
              return BaasEdgePoolAndCluster.updateBaasPool({
                poolId: poolId,
                displayName: null,
                domainChange: { change: DomainUpdateType.Remove, domainId: domainName as DomainID },
                DataConnectSettingsVersion: '0'
              }).then((result: IUpdateBaasEdgePoolResult) => {
                return Promise.resolve({}); //errors
              });
            }
            return Promise.resolve({});
          });
        } else {
          ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsAddDomainSuccess() });
          refreshPoolsAndClusters();
        }
      }
      if (result.errors) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
        });
        throw new Error(getGeneralErrorMessage(result.errors[0].code, result.errors[0].message));
      }
      return Promise.resolve({});
    });
  };

  const onTestConnection = (fileServer: string, username: string, password: string) => {
    let fullPath: string = '//' + fileServer;

    // Extract domain from username (format: domain\username, domain/username)
    let extractedDomain = '';
    let extractedUsername = username;
    if (username) {
      // Check for domain\username or domain/username format
      const domainMatch = username.match(/^([^\\\/]+)[\\\/](.+)$/);
      if (domainMatch && domainMatch[1]) {
        extractedDomain = domainMatch[1];
        extractedUsername = domainMatch[2];
      }
    }

    return BaasEdgePoolAndCluster.setSmbVolumes({
      poolId: poolId,
      path: fullPath,
      domain: extractedDomain,
      username: extractedUsername,
      password: password,
      dryRun: true
    })
      .then((result: ISetSmbVolumesResult) => {
        if (result.success) {
          return { message: Messages.dataConnectSiteSettingsDomainFileServerTestConnectionSuccess(), messageType: InlineAlertType.Success };
        }
        if (result.errors) {
          return { message: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message), messageType: InlineAlertType.Error };
        }
        return { message: Messages.dataConnectSiteSettingsDomainFileServerTestConnection(), messageType: InlineAlertType.Error };
      });
  };

  const domainTableItems: ReadonlyArray<DomainTableItem> = createDomainTableItems(userDefinedPools, {});
  const bridgeDomainNames: ReadonlyArray<string> = Object.keys(bridgeDomains);
  openAddNewDomainDialog(onAddDomainDialogSave, onTestConnection,
    domainTableItems.map((item: DomainTableItem) => item.domainName), bridgeDomainNames, poolId, poolName);
}
